/**
 * Centralized API service for all backend communications
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:56165/api';

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T = any>(
    endpoint: string,
    options: ApiOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    // Build URL with query params
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    // Default options
    const defaultOptions: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url.toString(), {
        ...defaultOptions,
        ...fetchOptions,
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (!response.ok) {
          throw new ApiError(response.status, `HTTP error! status: ${response.status}`);
        }
        return response.text() as unknown as T;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(
          response.status,
          data.error || `HTTP error! status: ${response.status}`,
          data
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new ApiError(0, error.message);
      }
      throw new ApiError(0, 'An unknown error occurred');
    }
  }

  // HTTP methods
  async get<T = any>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async patch<T = any>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// Create singleton instance
const api = new ApiService();

// Export specific API modules
export const authApi = {
  login: (data: { email?: string; phone?: string; password?: string }) =>
    api.post('/auth/login', data),
  
  logout: () => api.post('/auth/logout'),
  
  signup: (data: { email: string; password: string; name: string }) =>
    api.post('/auth/signup', data),
  
  verifyOtp: (data: { phone: string; code: string }) =>
    api.post('/auth/verify-otp', data),
  
  sendOtp: (data: { phone: string }) =>
    api.post('/auth/send-otp', data),
};

export const userApi = {
  getProfile: () => api.get('/users/profile'),
  
  updateProfile: (data: any) => api.put('/users/profile', data),
  
  getLinkedAccounts: () => api.get('/users/linked-accounts'),
  
  linkAccount: (data: any) => api.post('/users/link-account', data),
  
  unlinkAccount: (accountId: string) => api.delete(`/users/unlink-account/${accountId}`),
  
  getNotificationSettings: () => api.get('/users/notification-settings'),
  
  updateNotificationSettings: (data: any) => api.put('/users/notification-settings', data),
  
  getPaymentMethods: () => api.get('/users/payment-methods'),
  
  addPaymentMethod: (data: any) => api.post('/users/payment-methods', data),
  
  setDefaultPaymentMethod: (methodId: string) => api.put(`/users/payment-methods/${methodId}/default`),
  
  deletePaymentMethod: (methodId: string) => api.delete(`/users/payment-methods/${methodId}`),
  
  getSubscription: () => api.get('/users/subscription'),
  
  updateSubscription: (data: any) => api.put('/users/subscription', data),
  
  cancelSubscription: () => api.post('/users/subscription/cancel'),
  
  getInviteStats: () => api.get('/users/invite-stats'),
  
  sendInvite: (data: { method: string; recipient: string; referralCode?: string }) => 
    api.post('/users/send-invite', data),
  
  sendFeedback: (data: { type: string; message: string }) => api.post('/users/feedback', data),
};

export const searchApi = {
  search: (params: { q: string; type?: string; filters?: any }) =>
    api.get('/search', { params }),
  
  autocomplete: (params: { q: string; type?: string }) =>
    api.get('/search/autocomplete', { params }),
};

export const providersApi = {
  search: (params: any) => api.get('/providers/search', { params }),
  
  getById: (id: string) => api.get(`/providers/${id}`),
  
  getByNPI: (npi: string) => api.get(`/providers/npi/${npi}`),
  
  getByTitleAndUsername: (titles: string, username: string) => 
    api.get(`/providers/titles/${titles}/${username}`),
  
  getAvailability: (id: string, params?: any) =>
    api.get(`/providers/${id}/availability`, { params }),
  
  scheduleAppointment: (id: string, data: any) =>
    api.post(`/providers/${id}/appointments`, data),
  
  rateProvider: (id: string, data: any) =>
    api.post(`/providers/${id}/ratings`, data),
};

export const proceduresApi = {
  search: (params: any) => api.get('/procedures/search', { params }),
  
  getById: (id: string) => api.get(`/procedures/${id}`),
  
  getPricing: (id: string, params?: any) =>
    api.get(`/procedures/${id}/pricing`, { params }),
};

export const medicationsApi = {
  search: (params: any) => api.get('/medications/search', { params }),
  
  getById: (id: string) => api.get(`/medications/${id}`),
  
  getPricing: (id: string, params?: any) =>
    api.get(`/medications/${id}/pricing`, { params }),
  
  getAlternatives: (id: string) => api.get(`/medications/${id}/alternatives`),
};

export const claimsApi = {
  getAll: (params?: any) => api.get('/claims', { params }),
  
  getById: (id: string) => api.get(`/claims/${id}`),
  
  getSummary: (params?: { year?: string }) => api.get('/claims/summary', { params }),
  
  dispute: (id: string, data: any) => api.post(`/claims/${id}/dispute`, data),
  
  getEobDocument: (id: string) => api.get(`/claims/${id}/eob`),
};

export const savedApi = {
  getLists: () => api.get('/saved/lists'),
  
  createList: (data: { name: string; description?: string }) =>
    api.post('/saved/lists', data),
  
  getListById: (id: string) => api.get(`/saved/lists/${id}`),
  
  updateList: (id: string, data: any) => api.put(`/saved/lists/${id}`, data),
  
  deleteList: (id: string) => api.delete(`/saved/lists/${id}`),
  
  addToList: (listId: string, data: { itemType: string; itemId: string }) =>
    api.post(`/saved/lists/${listId}/items`, data),
  
  removeFromList: (listId: string, itemId: string) =>
    api.delete(`/saved/lists/${listId}/items/${itemId}`),
  
  shareList: (listId: string, data: { email: string; message?: string }) =>
    api.post(`/saved/lists/${listId}/share`, data),
};

export const insuranceApi = {
  getPlans: () => api.get('/insurance/plans'),
  
  getPlanById: (id: string) => api.get(`/insurance/plans/${id}`),
  
  checkCoverage: (data: { procedureId?: string; medicationId?: string; planId: string }) =>
    api.post('/insurance/check-coverage', data),
};

export const aiFollowupApi = {
  createSession: (data: { query: string; context?: string }) =>
    api.post('/ai-followup/session', data),
  
  getSession: (sessionId: string) => 
    api.get(`/ai-followup/session/${sessionId}`),
  
  getQuestion: (sessionId: string, previousQuestionId?: string) => 
    api.get(`/ai-followup/session/${sessionId}/question`, 
      previousQuestionId ? { params: { previousQuestionId } } : undefined
    ),
  
  submitAnswer: (data: { sessionId: string; questionId: string; answer: any }) =>
    api.post('/ai-followup/answer', data),
  
  goToPrevious: (data: { sessionId: string; currentQuestionId: string }) =>
    api.post('/ai-followup/previous', data),
  
  getCarePlan: (sessionId: string) => 
    api.get(`/ai-followup/session/${sessionId}/care-plan`),
};

// Export the main api instance for custom requests
export default api;

// Export error class
export { ApiError };